import { Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { ScheduleHoursService } from '../../services/schedule-hours.service';
import { DayHours, HoursFitter, HoursResponse, HoursRow, toDayHours } from '../../models/hours.models';
import { PresetKey, SHIFT_PRESETS, Shift, overlaps, presetsOn, splitAcrossMidnight, togglePreset } from '../../board/hours-presets';
import { toIsoDay } from '../../models/schedule.models';
import { hhmm } from '../../board/board-geometry';

/** Whose hours a table edits: the workshop, or a fitter with Own hours. */
type Owner = { kind: 'workshop' } | { kind: 'fitter'; fitter: HoursFitter };

/** One day of a table: a date of the shown week, or a weekday of the usual week. */
interface DayView {
  key: string;
  label: string;
  /** YYYY-MM-DD in week mode; null in usual-week mode. */
  date: string | null;
  dayOfWeek: number;
  hours: DayHours;
  /** The date has its own change (week mode), so Default can put it back. */
  overridden: boolean;
  /** The following day's hours (a night shift writes its 00:00-06:00 there). */
  next: DayHours;
  nextDate: string | null;
}

const WEEKDAY_CHIPS = [
  { day: 1, label: 'Mon' }, { day: 2, label: 'Tue' }, { day: 3, label: 'Wed' }, { day: 4, label: 'Thu' },
  { day: 5, label: 'Fri' }, { day: 6, label: 'Sat' }, { day: 0, label: 'Sun' },
];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const USUAL_ORDER = [1, 2, 3, 4, 5, 6, 0];
const FALLBACK_SHIFT: Shift = { startMin: 7 * 60, endMin: 18 * 60 };

/**
 * Scheduler Hours - when the workshop and each fitter work, modelled on CGL's "Schedule hours" page
 * (docs/superpowers/specs/2026-10-21-scheduler-hours-design.md).
 *
 * Two layers, as in CGL: the USUAL WEEK (changing Monday changes every Monday) and a CHANGE ON A DATE (a bank
 * holiday, an overtime Saturday - that day only; Default removes it). Both exist for the workshop and again
 * for each fitter with Own hours. In "This week" mode an edit writes a change on that date; in "Usual week"
 * mode (an addition to CGL, where the usual week lives in the database) it edits the usual week itself.
 */
@Component({
    selector: 'app-scheduler-hours',
    templateUrl: './scheduler-hours.component.html',
    styleUrls: ['./scheduler-hours.component.scss'],
    standalone: false
})
export class SchedulerHoursComponent implements OnInit, OnDestroy {
  readonly presets = SHIFT_PRESETS;
  readonly weekdayChips = WEEKDAY_CHIPS;
  readonly hhmm = hhmm;

  readonly mode = signal<'week' | 'usual'>('week');
  readonly weekStart = signal<string>(mondayOf(toIsoDay(new Date())));
  readonly data = signal<HoursResponse | null>(null);
  readonly loading = signal<boolean>(true);
  readonly error = signal<string>('');
  readonly notice = signal<string>('');
  /** A save or reload is on its way: edits wait, so none is built on the old hours. */
  readonly busy = signal<boolean>(false);
  readonly workshopOpen = signal<boolean>(true);
  readonly openFitterId = signal<number | null>(null);

  // Block of days
  readonly rangeOpen = signal<boolean>(false);
  readonly rangeFrom = signal<string>('');
  readonly rangeTo = signal<string>('');
  readonly rangeDays = signal<Set<number>>(new Set([1, 2, 3, 4, 5]));
  readonly rangeAction = signal<'closed' | 'open' | 'default'>('closed');
  readonly rangeReason = signal<string>('');
  readonly rangeShift = signal<PresetKey | 'custom'>('day');
  readonly rangeStart = signal<string>('07:00');
  readonly rangeEnd = signal<string>('18:00');
  readonly rangeTarget = signal<'workshop' | 'fitters'>('workshop');
  readonly picked = signal<Set<number>>(new Set());
  readonly rangeBusy = signal<boolean>(false);

  readonly weekDates = computed(() => Array.from({ length: 7 }, (_, i) => addDays(this.weekStart(), i)));
  readonly weekLabel = computed(() => {
    const d = this.weekDates();
    return `${formatDate(d[0], { day: 'numeric', month: 'short' })} – ${formatDate(d[6], { day: 'numeric', month: 'short', year: 'numeric' })}`;
  });
  readonly fitters = computed(() => this.data()?.fitters ?? []);
  readonly workshopOwner: Owner = { kind: 'workshop' };

  /** Days the Block of days panel would change. */
  readonly rangeDates = computed(() => {
    const from = this.rangeFrom();
    const to = this.rangeTo();
    if (!from || !to || to < from || dayCount(from, to) > 366) {
      return [];
    }
    const out: string[] = [];
    for (let d = from; d <= to; d = addDays(d, 1)) {
      if (this.rangeDays().has(dayOfWeek(d))) {
        out.push(d);
      }
    }
    return out;
  });

  private noticeTimer: any;
  /** Only the newest load() may fill the page (quick week steps can answer out of order). */
  private loadSeq = 0;

  constructor(private service: ScheduleHoursService) { }

  ngOnInit(): void {
    this.resetRangeDates();
    this.load();
  }

  ngOnDestroy(): void {
    clearTimeout(this.noticeTimer);
  }

  load(): void {
    const seq = ++this.loadSeq;
    this.loading.set(this.data() === null);
    this.busy.set(true);
    // one day past the week, so a Sunday night shift can see Monday
    this.service.get(this.weekStart(), addDays(this.weekStart(), 7)).pipe(take(1)).subscribe({
      next: (data) => {
        if (seq !== this.loadSeq) {
          return;
        }
        this.data.set(data);
        this.loading.set(false);
        this.busy.set(false);
        this.error.set('');
      },
      error: (e) => {
        if (seq !== this.loadSeq) {
          return;
        }
        this.loading.set(false);
        this.busy.set(false);
        this.error.set(message(e, 'The hours could not be loaded.'));
      },
    });
  }

  // --- week navigation ------------------------------------------------------------------------------

  stepWeek(n: number): void {
    this.weekStart.set(addDays(this.weekStart(), 7 * n));
    this.resetRangeDates();
    this.load();
  }

  thisWeek(): void {
    this.weekStart.set(mondayOf(toIsoDay(new Date())));
    this.resetRangeDates();
    this.load();
  }

  onWeekPick(value: string): void {
    if (value) {
      this.weekStart.set(mondayOf(value));
      this.resetRangeDates();
      this.load();
    }
  }

  // --- tables ---------------------------------------------------------------------------------------

  /** The seven days of a table, in the current mode. */
  days(owner: Owner): DayView[] {
    const data = this.data();
    if (!data) {
      return [];
    }
    const pattern = owner.kind === 'workshop' ? data.workshop.pattern : owner.fitter.pattern;
    const dated = owner.kind === 'workshop' ? data.workshop.days : owner.fitter.days;
    const weekday = (dow: number) => toDayHours(pattern.filter((r) => r.dayOfWeek === dow));
    if (this.mode() === 'usual') {
      return USUAL_ORDER.map((dow) => ({
        key: 'dow' + dow, label: DAY_NAMES[dow], date: null, dayOfWeek: dow,
        hours: weekday(dow), overridden: false, next: weekday((dow + 1) % 7), nextDate: null,
      }));
    }
    const onDate = (date: string) => {
      const rows = dated.filter((r) => r.workDate === date);
      return { rows, hours: rows.length ? toDayHours(rows) : weekday(dayOfWeek(date)) };
    };
    return this.weekDates().map((date) => {
      const today = onDate(date);
      const nextDate = addDays(date, 1);
      return {
        key: date, label: DAY_NAMES[dayOfWeek(date)], date, dayOfWeek: dayOfWeek(date),
        hours: today.hours, overridden: today.rows.length > 0, next: onDate(nextDate).hours, nextDate,
      };
    });
  }

  /** Dates in the shown week with their own change. */
  changedCount(rows: HoursRow[]): number {
    const week = new Set(this.weekDates());
    return new Set(rows.filter((r) => week.has(r.workDate)).map((r) => r.workDate)).size;
  }

  presetsOnDay(day: DayView): Set<PresetKey> {
    return presetsOn(day.hours.shifts, day.next.shifts);
  }

  timeValue(min: number | null): string {
    return min === null ? '' : hhmm(min % 1440);
  }

  dateLabel(date: string): string {
    return date.split('-').reverse().join('/');
  }

  ownerKey(owner: Owner): string {
    return owner.kind === 'workshop' ? 'workshop' : 'fitter' + owner.fitter.id;
  }

  // --- edits (each saves straight away, then the page reloads) -----------------------------------------

  onTime(owner: Owner, day: DayView, index: number, which: 'start' | 'end', input: HTMLInputElement): void {
    const before = day.hours.shifts[index][which === 'start' ? 'startMin' : 'endMin'];
    const putBack = (text: string) => {
      input.value = this.timeValue(before); // the binding has not changed, so put the box back by hand
      this.refuse(text);
    };
    if (!input.value) {
      putBack('Give a time.');
      return;
    }
    let min = toMin(input.value);
    if (which === 'end' && min === 0) {
      min = 1440; // an end of 00:00 is midnight
    }
    const shifts = day.hours.shifts.map((s, i) => i === index ? { ...s, [which === 'start' ? 'startMin' : 'endMin']: min } : s);
    const s = shifts[index];
    if (s.endMin <= s.startMin) {
      putBack('A shift must end after it starts – for a night shift use the Night button, or two shifts split at midnight.');
      return;
    }
    if (overlaps(shifts)) {
      putBack('Shifts on the same day cannot overlap.');
      return;
    }
    this.saveDay(owner, day, { closed: false, closedReason: null, shifts });
  }

  onClosed(owner: Owner, day: DayView, closed: boolean): void {
    if (closed) {
      this.saveDay(owner, day, { closed: true, closedReason: null, shifts: [] });
    } else {
      this.saveDay(owner, day, { closed: false, closedReason: null, shifts: this.openingShifts(owner, day) });
    }
  }

  onReason(owner: Owner, day: DayView, reason: string): void {
    this.saveDay(owner, day, { closed: true, closedReason: reason.trim() || null, shifts: [] });
  }

  addShift(owner: Owner, day: DayView): void {
    const last = day.hours.shifts[day.hours.shifts.length - 1];
    const start = last ? last.endMin : FALLBACK_SHIFT.startMin;
    if (day.hours.shifts.length >= 4 || start >= 1440) {
      this.refuse(day.hours.shifts.length >= 4 ? 'A day can have at most 4 shifts.' : 'There is no time left in this day for another shift.');
      return;
    }
    const shifts = day.hours.closed ? [FALLBACK_SHIFT] : [...day.hours.shifts, { startMin: start, endMin: Math.min(start + 240, 1440) }];
    this.saveDay(owner, day, { closed: false, closedReason: null, shifts });
  }

  removeShift(owner: Owner, day: DayView, index: number): void {
    this.saveDay(owner, day, { closed: false, closedReason: null, shifts: day.hours.shifts.filter((_, i) => i !== index) });
  }

  applyPreset(owner: Owner, day: DayView, key: PresetKey): void {
    const r = togglePreset(day.hours.shifts, day.next.shifts, key);
    if (!r) {
      this.refuse('That shift overlaps hours this day (or the next) already has, or would make more than 4 – change or remove them first.');
      return;
    }
    const asDay = (shifts: Shift[]): DayHours => shifts.length ? { closed: false, closedReason: null, shifts } : { closed: true, closedReason: null, shifts: [] };
    const usual = this.mode() === 'usual' || !day.date;
    const at = (date: string | null, dow: number) => usual ? { dayOfWeek: dow } : { date };
    const writes = [{ ...at(day.date, day.dayOfWeek), day: asDay(r.today) }];
    if (r.nextChanged) {
      writes.push({ ...at(day.nextDate, (day.dayOfWeek + 1) % 7), day: asDay(r.next) });
    }
    // one call, so a night shift is never half saved
    this.run(this.service.saveDays(ownerId(owner), writes), 'Saved');
  }

  /** Default: the date goes back to the usual week. */
  reset(owner: Owner, day: DayView): void {
    this.run(this.service.resetDate(ownerId(owner), day.date), 'Back to the usual week');
  }

  onOwnHours(fitter: HoursFitter, on: boolean): void {
    if (on) {
      // starts as a copy of the workshop's usual week
      const week = [0, 1, 2, 3, 4, 5, 6].map((dow) => toDayHours(this.data().workshop.pattern.filter((r) => r.dayOfWeek === dow)));
      this.openFitterId.set(fitter.id);
      this.run(this.service.setOwnWeek(fitter.id, week), `${fitter.name} now has their own hours`);
    } else {
      if (!confirm(`${fitter.name} will follow the workshop week again. Their own usual week is removed (changes on particular dates stay). Carry on?`)) {
        this.load();
        return;
      }
      this.openFitterId.set(null);
      this.run(this.service.clearOwnWeek(fitter.id), `${fitter.name} follows the workshop week`);
    }
  }

  toggleFitter(fitter: HoursFitter): void {
    if (fitter.ownHours) {
      this.openFitterId.set(this.openFitterId() === fitter.id ? null : fitter.id);
    }
  }

  // --- Block of days -------------------------------------------------------------------------------

  isWeekdayOn(day: number): boolean {
    return this.rangeDays().has(day);
  }

  toggleWeekday(day: number): void {
    const next = new Set(this.rangeDays());
    next.has(day) ? next.delete(day) : next.add(day);
    this.rangeDays.set(next);
  }

  setWeekdays(days: number[]): void {
    this.rangeDays.set(new Set(days));
  }

  isPicked(id: number): boolean {
    return this.picked().has(id);
  }

  togglePicked(id: number): void {
    const next = new Set(this.picked());
    next.has(id) ? next.delete(id) : next.add(id);
    this.picked.set(next);
  }

  pickAll(on: boolean): void {
    this.picked.set(new Set(on ? this.fitters().map((f) => f.id) : []));
  }

  /** The shifts the panel would write, and the part carried into the next day. */
  rangeShifts(): { today: Shift[]; next: Shift[] } | null {
    const key = this.rangeShift();
    if (key !== 'custom') {
      const p = SHIFT_PRESETS.find((x) => x.key === key);
      return { today: p.today, next: p.next };
    }
    if (!this.rangeStart() || !this.rangeEnd()) {
      return null;
    }
    const start = toMin(this.rangeStart());
    const end = toMin(this.rangeEnd());
    return start === end ? null : splitAcrossMidnight(start, end);
  }

  /** What Apply will do, before it is pressed. */
  rangeSummary(): string {
    const dates = this.rangeDates();
    if (!this.rangeFrom() || !this.rangeTo() || this.rangeTo() < this.rangeFrom()) {
      return 'Choose the dates.';
    }
    if (dayCount(this.rangeFrom(), this.rangeTo()) > 366) {
      return 'That range is longer than a year – shorten it.';
    }
    if (!dates.length) {
      return 'None of those dates fall on the days chosen.';
    }
    const who = this.rangeTarget() === 'workshop' ? 'the workshop'
      : this.picked().size === 1 ? this.fitters().find((f) => this.picked().has(f.id))?.name
      : `${this.picked().size} fitters`;
    if (this.rangeTarget() === 'fitters' && !this.picked().size) {
      return 'Choose the fitters.';
    }
    const span = `${dates.length} day${dates.length === 1 ? '' : 's'} from ${this.dateLabel(dates[0])} to ${this.dateLabel(dates[dates.length - 1])}`;
    switch (this.rangeAction()) {
      case 'closed':
        return `Close ${who} on ${span}${this.rangeReason().trim() ? ` – ${this.rangeReason().trim()}` : ''}.`;
      case 'default':
        return `Put ${who} back to the usual week on ${span}.`;
      default: {
        const s = this.rangeShifts();
        if (!s) {
          return 'Give the start and end times.';
        }
        const times = [...s.today].map((w) => `${hhmm(w.startMin)}–${hhmm(w.endMin)}`).join(', ');
        return `Set ${who} to ${times}${s.next.length ? ` (running on to ${hhmm(s.next[0].endMin)} the next morning)` : ''} on ${span}.`;
      }
    }
  }

  canApplyRange(): boolean {
    return !this.rangeBusy() && this.rangeDates().length > 0
      && (this.rangeTarget() === 'workshop' || this.picked().size > 0)
      && (this.rangeAction() !== 'open' || !!this.rangeShifts());
  }

  applyRange(): void {
    const shifts = this.rangeShifts();
    this.rangeBusy.set(true);
    this.service.applyRange({
      from: this.rangeFrom(), to: this.rangeTo(), weekdays: [...this.rangeDays()], action: this.rangeAction(),
      closedReason: this.rangeReason().trim() || undefined,
      shifts: this.rangeAction() === 'open' ? shifts.today : undefined,
      nextDayShifts: this.rangeAction() === 'open' && shifts.next.length ? shifts.next : undefined,
      target: this.rangeTarget(), fitterIds: this.rangeTarget() === 'fitters' ? [...this.picked()] : undefined,
    }).pipe(take(1)).subscribe({
      next: (r) => {
        this.rangeBusy.set(false);
        this.toast(`Changed ${r.changed} day${r.changed === 1 ? '' : 's'}`);
        this.load();
      },
      error: (e) => {
        this.rangeBusy.set(false);
        this.toast(message(e, 'Those days were not changed.'));
      },
    });
  }

  private resetRangeDates(): void {
    this.rangeFrom.set(this.weekStart());
    this.rangeTo.set(addDays(this.weekStart(), 6));
  }

  // --- saving ----------------------------------------------------------------------------------------

  private saveDay(owner: Owner, day: DayView, hours: DayHours): void {
    this.run(this.write(owner, day.date, day.dayOfWeek, hours), 'Saved');
  }

  /** A date in week mode, the weekday of the usual week in usual-week mode. */
  private write(owner: Owner, date: string | null, dow: number, hours: DayHours): Observable<unknown> {
    return this.mode() === 'usual' || !date
      ? this.service.saveWeekday(ownerId(owner), dow, hours)
      : this.service.saveDate(ownerId(owner), date, hours);
  }

  /** Reopening a closed day: the usual week's shifts for that weekday if it has any, else 07:00-18:00. */
  private openingShifts(owner: Owner, day: DayView): Shift[] {
    const pattern = owner.kind === 'workshop' ? this.data().workshop.pattern : owner.fitter.pattern;
    const usual = toDayHours(pattern.filter((r) => r.dayOfWeek === day.dayOfWeek));
    return !usual.closed && this.mode() === 'week' ? usual.shifts : [FALLBACK_SHIFT];
  }

  private run(request: Observable<unknown>, done: string): void {
    if (this.busy()) {
      this.toast('Still saving the last change – try again in a moment.');
      this.load(); // put the inputs back to what is saved
      return;
    }
    this.busy.set(true);
    request.pipe(take(1)).subscribe({
      next: () => {
        this.toast(done);
        this.load();
      },
      error: (e) => {
        this.toast(message(e, 'That change was not saved.'));
        this.load();
      },
    });
  }

  private refuse(text: string): void {
    this.toast(text);
    this.load(); // put the inputs back
  }

  private toast(text: string): void {
    this.notice.set(text);
    clearTimeout(this.noticeTimer);
    this.noticeTimer = setTimeout(() => this.notice.set(''), 4000);
  }
}

function ownerId(owner: Owner): number | null {
  return owner.kind === 'workshop' ? null : owner.fitter.id;
}

function toMin(value: string): number {
  const [h, m] = value.split(':').map(Number);
  return h * 60 + m;
}

function parseDay(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function addDays(iso: string, n: number): string {
  const d = parseDay(iso);
  return toIsoDay(new Date(d.getFullYear(), d.getMonth(), d.getDate() + n));
}

function dayOfWeek(iso: string): number {
  return parseDay(iso).getDay();
}

function mondayOf(iso: string): string {
  return addDays(iso, -((dayOfWeek(iso) + 6) % 7));
}

function dayCount(from: string, to: string): number {
  return Math.round((parseDay(to).getTime() - parseDay(from).getTime()) / 86400000);
}

function formatDate(iso: string, o: Intl.DateTimeFormatOptions): string {
  return parseDay(iso).toLocaleDateString('en-GB', o);
}

function message(e: any, fallback: string): string {
  return (e && e.error && e.error.message) || fallback;
}
